import logger from './logger';

/** Focus a ref after mount/portal render (retries until element is in the DOM). */
export function focusRefWhenReady(ref, { active = true, delays = [0, 50, 150, 300] } = {}) {
	if (!active) return () => {};
	let cancelled = false;
	const tryFocus = () => {
		if (cancelled) return;
		const el = ref?.current;
		if (el && typeof el.focus === 'function') {
			el.focus({ preventScroll: true });
		}
	};
	const timeoutIds = delays.map((ms) => setTimeout(tryFocus, ms));
	const rafId = requestAnimationFrame(tryFocus);
	return () => {
		cancelled = true;
		cancelAnimationFrame(rafId);
		timeoutIds.forEach(clearTimeout);
	};
}

/** Normalize time text for search (03:49 → 3:49, strips extra spaces). */
export function normalizeTimeSearchText(text) {
	return String(text || '')
		.toLowerCase()
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/(\d{1,2}):(\d{0,2})/g, (match, hourPart, minutePart) => {
			const hour = parseInt(hourPart, 10);
			if (Number.isNaN(hour)) return match;
			if (minutePart === '' && match.endsWith(':')) {
				return `${hour}:`;
			}
			const minute = parseInt(minutePart, 10);
			if (Number.isNaN(minute)) return `${hour}:`;
			return `${hour}:${String(minute).padStart(2, '0')}`;
		});
}

/** True when value looks like an outlet record (id + name), not a wrapper/error. */
export function isOutletRecord(value) {
	return Boolean(
		value &&
		typeof value === "object" &&
		!Array.isArray(value) &&
		value.id != null &&
		Number.isFinite(Number(value.id))
	);
}

/**
 * APICalling already returns axios `response.data`. Local APIs often nest the
 * outlet at `data`; some environments nest it at `data.data`.
 */
export function extractOutletFromApiResponse(response) {
	if (!response || typeof response !== "object") return null;
	if (response.isAxiosError || response instanceof Error) {
		return extractOutletFromApiResponse(response.response?.data);
	}
	const candidates = [response?.data?.data, response?.data, response];
	return candidates.find(isOutletRecord) || null;
}

export function findAssignedOutletById(outlets, outletId) {
	const normalizedId = Number(outletId);
	if (!Number.isFinite(normalizedId) || normalizedId <= 0) return null;
	return (Array.isArray(outlets) ? outlets : []).find(
		(outlet) => Number(outlet?.id) === normalizedId
	) || null;
}

/** Dropdown filter: supports leading-zero times (e.g. 03:49 matches 3:49 PM, 02 matches 2:xx). */
export function optionLabelMatchesSearch(label, query) {
	const labelText = String(label ?? '');
	const queryText = String(query ?? '').trim();
	if (!queryText) return true;

	const labelLower = labelText.toLowerCase();
	const queryLower = queryText.toLowerCase();
	if (labelLower.includes(queryLower)) return true;

	const compactLabel = labelLower.replace(/\s+/g, '');
	const compactQuery = queryLower.replace(/\s+/g, '');
	if (compactLabel.includes(compactQuery)) return true;

	// Hour-only: "02" / "2" matches "2:00 PM", "2:05 AM", etc.
	if (/^\d{1,2}$/.test(queryText)) {
		const queryHour = parseInt(queryText, 10);
		if (!Number.isNaN(queryHour)) {
			const hourMatches = [...labelLower.matchAll(/(?:^|\s)(\d{1,2}):/g)].some((match) => {
				return parseInt(match[1], 10) === queryHour;
			});
			if (hourMatches) return true;
		}
	}

	if (/\d/.test(queryText) && queryText.includes(':')) {
		const normalizedLabel = normalizeTimeSearchText(labelText);
		const normalizedQuery = normalizeTimeSearchText(queryText);
		if (normalizedLabel.includes(normalizedQuery)) return true;
		if (normalizedLabel.replace(/\s+/g, '').includes(normalizedQuery.replace(/\s+/g, ''))) {
			return true;
		}
	}

	return false;
}

// FOR STATUS CHANGED 
export function updateBookingStatus(setterFn, bookingId, newStatus) {
	if (bookingId == null || bookingId === "") return;

	const targetId = String(bookingId);
	setterFn((prevList) =>
		(Array.isArray(prevList) ? prevList : []).map((b) =>
			String(b.id ?? "") === targetId || String(b.booking_id ?? "") === targetId
				? { ...b, status: newStatus }
				: b
		)
	);
}

export function scrollToBooking(id) {
	window.dispatchEvent(
		new CustomEvent("scrollToBooking", {
			detail: {
				...id,
				booking_id: id,
			},
		})
	);
}

export function scrollToBookingCard(id, delay = 500, text) {
	const maxRetries = 60; // ~6s — allow Home calendar cold-load after date change
	const retryInterval = 100;
	const normalizedId = String(id);
	const tryScroll = (retriesLeft) => {
		const card = document.querySelector(`[data-item-id='${normalizedId}']`);
		if (card) {
			sessionStorage.removeItem("skipCurrentTimeAutoScroll");
			setTimeout(() => {
				logger.log("scrolling to booking card", id);
				card.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
				card.classList.add("highlightedCard");
				setTimeout(() => card.classList.remove("highlightedCard"), 500);
			}, delay);
			return;
		}
		if (retriesLeft > 0) {
			setTimeout(() => tryScroll(retriesLeft - 1), retryInterval);
		}
		// Do not clear skipCurrentTimeAutoScroll on failure — Home redirect scroll may still be pending
	};
	tryScroll(maxRetries);
}

export function scrollToNewBookingCard(id, delay = 50 ) { 
	const maxRetries = 20;
	const retryInterval = 100;
	const normalizedId = String(id);
	const tryScroll = (retriesLeft) => {
		const card = document.querySelector(`[data-item-id='${normalizedId}']`);
		if (card) {
			sessionStorage.removeItem("skipCurrentTimeAutoScroll");
			setTimeout(() => {
				card.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
				card.classList.add("highlightedCard");
				setTimeout(() => card.classList.remove("highlightedCard"), 500);
			}, delay);
			return;
		}
		if (retriesLeft > 0) {
			setTimeout(() => tryScroll(retriesLeft - 1), retryInterval);
		}
		// Do not clear skipCurrentTimeAutoScroll on failure — date-change scroll may still be pending
	};
	tryScroll(maxRetries);
}














export function convertTo12Hour(time) {
	if (!time || typeof time !== "string") {
		return "Select Time";
	}
	const [hour, minute] = time.toString().split(":");
	if (!hour || !minute) return "--";
	const hourInt = parseInt(hour, 10);
	if (Number.isNaN(hourInt)) return "--";
	const normalizedHour = ((hourInt % 24) + 24) % 24;
	const ampm = normalizedHour >= 12 ? "PM" : "AM";
	const hourFormatted = normalizedHour % 12 || 12;
	return `${hourFormatted}:${minute} ${ampm}`;
}


export const convertTo24HourFormat = (time12h) => {
	if (typeof time12h !== "string") {
		logger.error("Invalid time format received:", time12h);
		return ""; // Prevent crash
	}
	const [time, modifier] = time12h.split(" ");
	if (!time || !modifier) return "";
	let [hours, minutes] = time.split(":");
	if (!minutes) minutes = "00";
	if (hours === "12") {
		hours = modifier === "AM" ? "00" : "12";
	} else if (modifier === "PM") {
		hours = String(parseInt(hours, 10) + 12);
	}
	return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const normalizeDropdownTimeValue = (timeValue) => {
	if (!timeValue || typeof timeValue !== "string") {
		return "";
	}

	const [hours = "", minutes = ""] = timeValue.split(":");
	if (!hours || !minutes) {
		return "";
	}

	return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
};

export const parseExtendedDropdownValue = (timeValue) => {
	if (!timeValue || typeof timeValue !== "string") {
		return { value: "", isNextDay: false };
	}

	const isNextDay = timeValue.endsWith("+1");
	const normalizedValue = normalizeDropdownTimeValue(
		isNextDay ? timeValue.slice(0, -2) : timeValue
	);

	return {
		value: normalizedValue,
		isNextDay,
	};
};

export const stripTimeSelectionDayOffsetSuffix = (label = "") => (
	String(label).replace(/\s*(?:\(\+\d+\)|\+\d+)(?:\s*(?:\(\+\d+\)|\+\d+))*\s*$/, "").trim()
);

export const getTimeSelectionDayOffset = (timeLike) => {
	if (!timeLike) return 0;

	if (typeof timeLike === "object") {
		if (typeof timeLike.absoluteMinutes === "number" && !Number.isNaN(timeLike.absoluteMinutes)) {
			return Math.floor(timeLike.absoluteMinutes / 1440);
		}
		if (typeof timeLike.dayOffset === "number" && !Number.isNaN(timeLike.dayOffset)) {
			return timeLike.dayOffset;
		}
	}

	const source = typeof timeLike === "string"
		? timeLike
		: `${timeLike?.label || ""} ${timeLike?.value || ""}`;
	const match = String(source).match(/\+(\d+)/);
	return match ? Number(match[1]) : 0;
};

/** Signed calendar-day difference (can be negative for previous date). */
export const getSignedDayOffsetFromDateStrings = (targetDateStr, baseDateStr) => {
	if (!targetDateStr || !baseDateStr) return 0;
	const parse = (value) => {
		const raw = String(value).trim();
		if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
			const [yyyy, mm, dd] = raw.split("-").map(Number);
			const date = new Date(yyyy, mm - 1, dd);
			if (Number.isNaN(date.getTime())) return null;
			date.setHours(0, 0, 0, 0);
			return date;
		}
		const [dd, mm, yyyy] = raw.split("-").map(Number);
		if (!dd || !mm || !yyyy) return null;
		const date = new Date(yyyy, mm - 1, dd);
		if (Number.isNaN(date.getTime())) return null;
		date.setHours(0, 0, 0, 0);
		return date;
	};
	const target = parse(targetDateStr);
	const base = parse(baseDateStr);
	if (!target || !base) return 0;
	return Math.round((target.getTime() - base.getTime()) / (24 * 60 * 60 * 1000));
};

/**
 * Absolute start minutes relative to the currently viewed calendar date.
 * Previous-date services keep a signed offset (yesterday 11:50 PM → -10).
 * Last-night expansion cards shown at 12:00–5:59 AM on today's grid sit at
 * local overnight minutes (12:00 AM → 0) so 11:50 snaps to yesterday.
 */
export const getViewRelativeAbsoluteStartMinutes = (
	service,
	baseDateStr,
	fallbackAbsoluteMinutes = null
) => {
	const startTime = service?.start_time
		? String(service.start_time).slice(0, 5)
		: (typeof service?.service_at === "string"
			? String(service.service_at).split(" ")[1]?.slice(0, 5) || ""
			: "");
	const dateFromServiceAt = typeof service?.service_at === "string"
		? String(service.service_at).split(" ")[0]
		: null;
	const startDateStr = service?.start_date || null;
	const signedFromStart = startDateStr && baseDateStr
		? getSignedDayOffsetFromDateStrings(startDateStr, baseDateStr)
		: null;
	const signedFromAt = dateFromServiceAt && baseDateStr
		? getSignedDayOffsetFromDateStrings(dateFromServiceAt, baseDateStr)
		: null;
	// Edit-load can rewrite a last-night expansion onto tomorrow (+1) while
	// service_at still has yesterday. Trust the earlier date in that case only.
	const dateStr =
		signedFromStart != null && signedFromAt != null && signedFromStart > 0 && signedFromAt < 0
			? dateFromServiceAt
			: (startDateStr || dateFromServiceAt);

	if (dateStr && startTime && baseDateStr) {
		const signedOffset = getSignedDayOffsetFromDateStrings(dateStr, baseDateStr);
		const clock = convertToMinutes(startTime);
		if (!Number.isNaN(clock)) {
			if (signedOffset < 0 && clock < 6 * 60) {
				return clock;
			}
			if (signedOffset !== 0) {
				return (signedOffset * 1440) + clock;
			}
		}
	}

	if (typeof service?.absoluteMinutes === "number" && !Number.isNaN(service.absoluteMinutes)) {
		return service.absoluteMinutes;
	}
	if (typeof fallbackAbsoluteMinutes === "number" && !Number.isNaN(fallbackAbsoluteMinutes)) {
		return fallbackAbsoluteMinutes;
	}
	if (startTime) {
		const clock = convertToMinutes(startTime);
		return Number.isNaN(clock) ? null : clock;
	}
	return null;
};

/**
 * Place a clock time on the nearest previous / booking-day / +1 timeline relative
 * to the service's current absolute start (view-relative, can be negative).
 *
 * Example: service at 12:00 AM +1 (1440). Pick 11:50 → same-day 23:50 (1430).
 * Pick 12:50 AM → next-day 00:50 (1490).
 * Example: viewing today, service at 12:00 AM (0). Pick 11:50 → previous-day 23:50 (-10).
 */
export const resolveNearestAbsoluteStartMinutes = (
	clockTime24,
	referenceAbsoluteMinutes = null,
	{ minDayOffset = -1, maxDayOffset = 1 } = {}
) => {
	const clock = convertToMinutes(extractHHMM(clockTime24) || clockTime24);
	if (Number.isNaN(clock)) return null;

	const hasReference =
		typeof referenceAbsoluteMinutes === "number" && !Number.isNaN(referenceAbsoluteMinutes);
	if (!hasReference) return null;

	const ref = referenceAbsoluteMinutes;
	const refDay = Math.floor(ref / 1440);
	const candidateDays = new Set();
	for (let day = refDay - 1; day <= refDay + 1; day += 1) {
		if (day >= minDayOffset && day <= maxDayOffset) candidateDays.add(day);
	}
	for (let day = minDayOffset; day <= maxDayOffset; day += 1) {
		candidateDays.add(day);
	}

	let bestAbs = null;
	let bestDist = Infinity;
	candidateDays.forEach((day) => {
		const abs = (day * 1440) + clock;
		const dist = Math.abs(abs - ref);
		if (dist < bestDist || (dist === bestDist && bestAbs != null && abs < bestAbs)) {
			bestDist = dist;
			bestAbs = abs;
		}
	});

	return bestAbs;
};

export const resolveNearestTimeSelection = (
	clockTime24,
	referenceAbsoluteMinutes = null,
	fallbackDayOffset = 0,
	explicitLabel = "",
	nearestOptions = { minDayOffset: -1, maxDayOffset: 1 }
) => {
	const nearestAbs = resolveNearestAbsoluteStartMinutes(
		clockTime24,
		referenceAbsoluteMinutes,
		nearestOptions
	);
	if (nearestAbs == null) {
		return buildTimeSelection(clockTime24, fallbackDayOffset, explicitLabel);
	}
	const dayOffset = Math.floor(nearestAbs / 1440);
	return buildTimeSelection(clockTime24, dayOffset, explicitLabel, nearestAbs);
};

export const buildTimeSelection = (
	timeValue,
	dayOffset = 0,
	explicitLabel = "",
	explicitAbsoluteMinutes = null
) => {
	if (!timeValue) return null;

	let normalizedValue = String(timeValue).trim();
	normalizedValue = stripTimeSelectionDayOffsetSuffix(normalizedValue);

	if (/am|pm/i.test(normalizedValue)) {
		normalizedValue = convertTo24HourFormat(normalizedValue);
	}

	if (!normalizedValue) return null;
	if (/^\d{1,2}:\d{2}(?::\d{2})?$/.test(normalizedValue)) {
		normalizedValue = normalizeDropdownTimeValue(normalizedValue);
	}

	const parsedOffset = Number(dayOffset);
	const normalizedDayOffset = Number.isNaN(parsedOffset) ? 0 : parsedOffset;
	// Always canonicalize the display label so dropdown options match regardless of source
	// (e.g. calendar "2 PM" vs dropdown "2:00 PM").
	const baseLabel = convertTo12Hour(normalizedValue);
	const absoluteMinutes = typeof explicitAbsoluteMinutes === "number" && !Number.isNaN(explicitAbsoluteMinutes)
		? explicitAbsoluteMinutes
		: (normalizedDayOffset * 1440) + convertToMinutes(normalizedValue);

	return {
		label: `${baseLabel}${normalizedDayOffset > 0 ? ` +${normalizedDayOffset}` : ""}`,
		value: normalizedValue,
		dayOffset: normalizedDayOffset,
		absoluteMinutes,
	};
};

export const findMatchingTimeOption = (timeSelection, options = []) => {
	if (!timeSelection?.value || !Array.isArray(options) || options.length === 0) {
		return null;
	}

	const dayOffset = getTimeSelectionDayOffset(timeSelection);
	let selectionMinutes = convertToMinutes(stripTimeSelectionDayOffsetSuffix(String(timeSelection.value)));
	if (Number.isNaN(selectionMinutes)) return null;
	selectionMinutes = Math.round(selectionMinutes / 5) * 5;

	const selectionTime24 = normalizeDropdownTimeValue(
		stripTimeSelectionDayOffsetSuffix(String(timeSelection.value))
	);

	if (timeSelection.label) {
		const byLabel = options.find((opt) => {
			const optLabel = typeof opt === "string" ? opt : opt.label;
			return optLabel === timeSelection.label;
		});
		if (byLabel) return byLabel;
	}

	return options.find((opt) => {
		const optDayOffset = typeof opt === "object" && typeof opt.dayOffset === "number"
			? opt.dayOffset
			: 0;
		const optLabel = typeof opt === "string" ? opt : opt.label;
		const optRawValue = typeof opt === "string" ? opt : (opt.timeValue || opt.value || "");
		const optTime24FromLabel = convertTo24HourFormat(stripTimeSelectionDayOffsetSuffix(optLabel));
		const optTime24FromValue = /am|pm/i.test(String(optRawValue))
			? convertTo24HourFormat(stripTimeSelectionDayOffsetSuffix(String(optRawValue)))
			: normalizeDropdownTimeValue(stripTimeSelectionDayOffsetSuffix(String(optRawValue)));
		const optTime24 = optTime24FromLabel || optTime24FromValue;
		const optMinutes = convertToMinutes(optTime24);
		return optDayOffset === dayOffset && (
			optMinutes === selectionMinutes ||
			(selectionTime24 && optTime24FromValue === selectionTime24)
		);
	}) || null;
};

export const normalizeTimeSelectionObject = (timeLike) => {
	if (!timeLike) return null;
	if (typeof timeLike === "object") {
		const rawValue = timeLike?.value || timeLike?.label || "";
		return buildTimeSelection(
			rawValue,
			getTimeSelectionDayOffset(timeLike),
			timeLike?.label || "",
			timeLike?.absoluteMinutes
		);
	}
	return buildTimeSelection(
		timeLike,
		getTimeSelectionDayOffset(timeLike),
		String(timeLike)
	);
};

export const doesTimeRangeCrossMidnight = (startTime, endTime) => {
	const normalizedStart = normalizeDropdownTimeValue(startTime);
	const normalizedEnd = normalizeDropdownTimeValue(endTime);

	if (!normalizedStart || !normalizedEnd) {
		return false;
	}

	return normalizedEnd < normalizedStart;
};

export const getOutletTimingDayKey = (dateInput) => {
	if (!dateInput) return "";

	let parsedDate = null;
	if (dateInput instanceof Date) {
		parsedDate = new Date(dateInput);
	} else if (typeof dateInput === "string") {
		if (/^\d{2}-\d{2}-\d{4}$/.test(dateInput)) {
			const [day, month, year] = dateInput.split("-").map(Number);
			parsedDate = new Date(year, month - 1, day);
		} else if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
			const [year, month, day] = dateInput.split("-").map(Number);
			parsedDate = new Date(year, month - 1, day);
		} else {
			parsedDate = new Date(dateInput);
		}
	} else {
		parsedDate = new Date(dateInput);
	}

	if (Number.isNaN(parsedDate.getTime())) {
		return "";
	}

	const weekdayKeys = [
		"sunday",
		"monday",
		"tuesday",
		"wednesday",
		"thursday",
		"friday",
		"saturday",
	];

	return weekdayKeys[parsedDate.getDay()] || "";
};

export const getOutletExtendedFlagForDate = (timingConfig, dateInput) => {
	const dayKey = getOutletTimingDayKey(dateInput);
	if (!dayKey || !timingConfig || typeof timingConfig !== "object") {
		return false;
	}

	const rawValue = timingConfig?.[dayKey]?.is_24hrs;
	return (
		rawValue === 1 ||
		rawValue === "1" ||
		rawValue === true ||
		rawValue === "true"
	);
};

export const shouldShowExpandedTimeLabel = (bookingLike, timingConfig, fallbackDateInput = null) => {
	const rawExpansionFlag =
		bookingLike?.is_calendar_expansion ??
		bookingLike?.isCalendarExpansion ??
		bookingLike?.calendar_expansion;

	const isExpanded =
		rawExpansionFlag === 1 ||
		rawExpansionFlag === "1" ||
		rawExpansionFlag === true ||
		rawExpansionFlag === "true";

	if (!isExpanded) {
		return false;
	}

	const serviceDateInput =
		bookingLike?.service_date ||
		bookingLike?.start_date ||
		(typeof bookingLike?.service_at === "string" ? bookingLike.service_at.split(" ")[0] : null) ||
		fallbackDateInput;

	return getOutletExtendedFlagForDate(timingConfig, serviceDateInput);
};

export const appendExpandedTimeLabel = (timeLabel, bookingLike, timingConfig, fallbackDateInput = null) => {
	if (!timeLabel) return "";
	if (!shouldShowExpandedTimeLabel(bookingLike, timingConfig, fallbackDateInput)) {
		return timeLabel;
	}
	return /\+\d+/.test(String(timeLabel)) ? timeLabel : `${timeLabel} +1`;
};

export const buildTimeDropdownOptions = ({
	isOutletExtended = false,
	includeNextDay = false,
	nextDayEndHour = 5,
} = {}) => {
	const options = [];

	for (let hour = 0; hour < 24; hour++) {
		for (let minute = 0; minute < 60; minute += 5) {
			const time24 = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
			options.push({
				label: convertTo12Hour(time24),
				value: time24,
				timeValue: time24,
				isNextDay: false,
			});
		}
	}

	if (!isOutletExtended || !includeNextDay) {
		return options;
	}

	for (let hour = 0; hour <= nextDayEndHour; hour++) {
		for (let minute = 0; minute < 60; minute += 5) {
			const time24 = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
			options.push({
				label: `${convertTo12Hour(time24)} (+1)`,
				value: `${time24}+1`,
				timeValue: time24,
				isNextDay: true,
			});
		}
	}

	return options;
};

export const getExtendedDropdownSelectedValue = ({
	value,
	anchorTime,
	isOutletExtended = false,
	crossesMidnight = false,
}) => {
	const normalizedValue = normalizeDropdownTimeValue(value);
	if (!normalizedValue) {
		return "";
	}

	if (!isOutletExtended || !crossesMidnight) {
		return normalizedValue;
	}

	const normalizedAnchor = normalizeDropdownTimeValue(anchorTime);
	if (!normalizedAnchor) {
		return normalizedValue;
	}

	return normalizedValue < normalizedAnchor ? `${normalizedValue}+1` : normalizedValue;
};



export const generateTimeSlots = (start, end, intervalMinutes) => {
	const slots = [];
	let currentTime = new Date(`1970-01-01T${start}:00`);
	let endTime = new Date(`1970-01-01T${end}:00`);
	if (end < start) {
		endTime.setDate(endTime.getDate() + 1);
	}
	while (currentTime <= endTime) {
		const timeString = currentTime.toTimeString().substring(0, 5);
		const formattedTime = convertTo12Hour(timeString);
		const isBold = currentTime.getMinutes() === 0; // Bold only on full hours
		slots.push({ time: formattedTime, isBold });
		currentTime.setMinutes(currentTime.getMinutes() + intervalMinutes);
	}
	return slots;
};


export const calculateMaxDate = (date) => {
	const maxDate = new Date(date);
	maxDate.setDate(maxDate.getDate() + 15);
	return maxDate;
};



export const filterStaff = (staffD, filteredGender, searchQuery) => {
	if (!staffD) return []; // Handle undefined/null input
	return staffD.filter((staff) => {
		const matchesGender =
			filteredGender === "all" ||
			staff.gender?.toLowerCase() === filteredGender.toLowerCase();

		const matchesSearch =
			staff.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
			staff.id.toString().includes(searchQuery);

		return matchesGender && matchesSearch;
	});
};

export const getCellKey = (rowIndex, colIndex) => `${rowIndex}-${colIndex}`;

//Create Booking Flow Functions

export const generateTimeSlotsNew = (start, end, intervalMinutes) => {
	const times = [];

	if (!start || !end) return times;

	let currentTime = new Date(`1970-01-01T${start}`);
	let endTime = new Date(`1970-01-01T${end}`);

	// If endTime is before startTime (e.g. overnight shift)
	if (endTime <= currentTime) {
		endTime.setDate(endTime.getDate() + 1);
	}

	while (currentTime < endTime) {
		const timeString = currentTime.toTimeString().substring(0, 5);
		const formattedTime = convertTo12Hour(timeString); // e.g. 08:30 AM

		times.push({ label: formattedTime, value: formattedTime });

		currentTime.setMinutes(currentTime.getMinutes() + intervalMinutes);
	}

	return times;
};


export const highlightQuery = (text, query) => {
	const trimmedQuery = String(query ?? "").trim();
	if (!text || !trimmedQuery) return text;

	// First escape special regex characters
	let escapedQuery = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

	// Then handle spaces (replace literal space with \s+)
	escapedQuery = escapedQuery.replace(/ /g, "\\s+");

	const parts = text.split(new RegExp(`(${escapedQuery})`, "gi"));

	return parts.map((part, index) =>
		part.toLowerCase() === trimmedQuery.toLowerCase() ? (
			<span key={index} style={{ color: "#bc6b38", fontWeight: "700" }}>
				{part}
			</span>
		) : (
			part
		)
	);
};



export const ensureDateObject = (input) => {
	if (input instanceof Date) return input;
	// If string in dd-mm-yyyy format
	if (typeof input === "string" && /^\d{2}-\d{2}-\d{4}$/.test(input)) {
		const [dd, mm, yyyy] = input.split("-").map(Number);
		return new Date(yyyy, mm - 1, dd);
	}

	logger.warn("Invalid date input passed to ensureDateObject:", input);
	return new Date(); // fallback
};

/** Parse service_at / redirect URL dates (DD-MM-YYYY, YYYY-MM-DD, or datetime). */
export const parseCalendarRedirectDate = (raw) => {
	if (!raw) return null;
	if (raw instanceof Date)return Number.isNaN(raw.getTime()) ? null : raw;
	const normalizedValue = String(raw).trim().replace("T", " ");
	const datePart = normalizedValue.split(/[ T]/)[0];

	if (/^\d{2}-\d{2}-\d{4}$/.test(datePart)) {
		const [dd, mm, yyyy] = datePart.split("-").map(Number);
		const parsed = new Date(yyyy, mm - 1, dd);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}

	if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
		const [yyyy, mm, dd] = datePart.split("-").map(Number);
		const parsed = new Date(yyyy, mm - 1, dd);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}

	const isoNormalized =String(raw).includes(" ") && !String(raw).includes("T") ? String(raw).replace(" ", "T"): raw;
	const parsed = new Date(isoNormalized);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const validateEmail = (email) => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
};

export const InputValidation = {
	hasEmoji(value = "") {
		return /[\p{Extended_Pictographic}\uFE0F]/u.test(String(value));
	},
	removeEmoji(value = "") {
		return String(value).replace(/[\p{Extended_Pictographic}\uFE0F]/gu, "");
	},
};

export const getFirstLastName = (fullName) => {
	const nameParts = fullName.split(" ");
	return {
		firstname: nameParts[0] || "",
		lastname: nameParts.slice(1).join(" ") || "",
	};
};




export const formatTimeWithoutZeroMinutes = (time24) => {
	const formattedTime = convertTo12Hour(time24);
	return formattedTime.replace(":00", ""); // Remove :00 only
};


export function convertToMinutes(timeStr) {
  // Return 0 if timeStr is invalid
  if (!timeStr || typeof timeStr !== "string") return 0;

  let hours, minutes;
  let is24Hour = true;

  // Check if it's 12-hour format (contains AM/PM)
  const [time, modifier] = timeStr.trim().split(" ");
  if (modifier && (modifier.toUpperCase() === "AM" || modifier.toUpperCase() === "PM")) {
		is24Hour = false;
    [hours, minutes] = time.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return 0;

    // Adjust hours for 12-hour format
    if (modifier.toUpperCase() === "PM" && hours !== 12) hours += 12;
    if (modifier.toUpperCase() === "AM" && hours === 12) hours = 0;
  } else {
    // Assume 24-hour format
    [hours, minutes] = timeStr.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return 0;
  }

  return hours * 60 + minutes;
}






export const generateFullDaySlotsWithOutletStatus = (
	dateObj,
	intervalMinutes,
	selectedOutletTiming,
	isOutletExtended = false
) => {
	const allSlots = [];
	const BASE_VISIBLE_CALENDAR_MINUTES = 23 * 60; // 11:00 PM
	const BASE_RENDER_CALENDAR_MINUTES = 24 * 60; // render full 11:00 PM block
	const MAX_VISIBLE_CALENDAR_MINUTES = 29 * 60; // last visible label is 5:00 AM (+1)
	const MAX_RENDER_CALENDAR_MINUTES = 30 * 60; // render full 5:00-6:00 AM block

	// Format date string as dd-mm-yyyy
	const dateStr = dateObj.toLocaleDateString("en-GB").split("/").join("-");
	const prevDate = new Date(dateObj);
	prevDate.setDate(prevDate.getDate() - 1);
	const prevDateStr = prevDate
		.toLocaleDateString("en-GB")
		.split("/")
		.join("-");

	// Get all timing entries for today (not just the first one)
	const todayTimings = selectedOutletTiming?.[dateStr] || [];
	const prevTimings = selectedOutletTiming?.[prevDateStr] || [];

	// Build array of open periods for the current date
	const openPeriods = [];
	let isOutletOff = true; // Start as true, will be false if any timing is on

	// Process all timing entries for today
	todayTimings.forEach((timing) => {
		if (timing.off === 1) {
			// This timing is off, skip it but don't mark entire outlet as off
			return;
		}

		// If we reach here, at least one timing is on
		isOutletOff = false;

		if (timing.open && timing.close) {
			const openMinutes = convertToMinutes(timing.open);
			const closeMinutes = convertToMinutes(timing.close);

			if (timing.open < timing.close) {
				// Normal time range (e.g., 09:00 to 17:00)
				openPeriods.push({
					start: openMinutes,
					end: closeMinutes,
				});
			} else {
				// Cross-day time range (e.g., 22:00 to 02:00 next day)
				openPeriods.push({
					start: openMinutes,
					end: Math.min(1440 + closeMinutes, MAX_RENDER_CALENDAR_MINUTES),
				});
			}
		}
	});

	// If no timings exist, check if outlet is off
	if (todayTimings.length === 0) {
		isOutletOff = true;
	}

	// Process previous day's cross-day entries that extend into today
	prevTimings.forEach((timing) => {
		if (timing.off === 1) return;

		if (timing.open && timing.close && timing.open > timing.close) {
			// This is a cross-day entry from previous day
			// The part after midnight belongs to today
			const closeMinutes = convertToMinutes(timing.close);
			openPeriods.push({
				start: convertToMinutes("00:00"),
				end: closeMinutes,
			});
		}
	});

	let timeSet = new Set();
	let maxVisibleMinutes = isOutletExtended
		? (MAX_RENDER_CALENDAR_MINUTES - intervalMinutes)
		: (BASE_RENDER_CALENDAR_MINUTES - intervalMinutes);

	// ADD slots based on interval as usual
	for (let currentMinutes = 0; currentMinutes <= maxVisibleMinutes; currentMinutes += intervalMinutes) {
		timeSet.add(currentMinutes);
	}

	const roundedMaxVisibleMinutes = Math.ceil(maxVisibleMinutes / intervalMinutes) * intervalMinutes;

	for (let currentMinutes = 0; currentMinutes <= roundedMaxVisibleMinutes; currentMinutes += intervalMinutes) {
		timeSet.add(currentMinutes);
	}

	// Convert to sorted array
	const sortedTimeList = Array.from(timeSet).sort((a, b) => a - b);

	// Helper function to check if a slot falls within any open period
	const isSlotInOpenPeriod = (slotStartMinutes, slotEndMinutes) => {
		if (isOutletOff) return false;
		if (openPeriods.length === 0) return false;

		return openPeriods.some((period) => {
			// Slot is enabled if it overlaps with any open period
			// Slot overlaps if: slotStart < period.end AND slotEnd > period.start
			// This ensures the slot has some overlap with the period
			const overlaps = slotStartMinutes < period.end && slotEndMinutes > period.start;
			return overlaps;
		});
	};

	// Generate full slot objects from sorted times
	for (const currentMinutes of sortedTimeList) {
		const displayHours = Math.floor((currentMinutes % 1440) / 60);
		const displayMinutes = currentMinutes % 60;
		const time24 = `${String(Math.floor(currentMinutes / 60)).padStart(2, "0")}:${String(displayMinutes).padStart(2, "0")}`;
		const displayTime24 = `${String(displayHours).padStart(2, "0")}:${String(displayMinutes).padStart(2, "0")}`;
		const formattedTime = convertTo12Hour(displayTime24);
		const isBold = currentMinutes % 60 === 0;
		const slotEndMinutes = currentMinutes + intervalMinutes;

		// Slot is disabled if it's not within any open period
		const disabled = !isSlotInOpenPeriod(currentMinutes, slotEndMinutes);

		// const partial =
		// 	!isOutletOff &&
		// 	currentMinutes < outletEnd &&
		// 	slotEndMinutes > outletEnd &&
		// 	currentMinutes >= outletStart;

		allSlots.push({
			time: formattedTime,
			time24,
			isBold: isBold && currentMinutes <= MAX_VISIBLE_CALENDAR_MINUTES,
			disabled,
			absoluteMinutes: currentMinutes,
			isExtended: currentMinutes >= 1440,
			isSpacer: currentMinutes > MAX_VISIBLE_CALENDAR_MINUTES,
			// partial,
		});
	}

	return allSlots;
};

export const addMinutesToTime = (timeStr, minutesToAdd) => {
	const [hours, minutes] = timeStr.split(":").map(Number);
	const date = new Date();
	date.setHours(hours);
	date.setMinutes(minutes + minutesToAdd);
	const newHours = date.getHours().toString().padStart(2, "0");
	const newMinutes = date.getMinutes().toString().padStart(2, "0");
	return `${newHours}:${newMinutes}`;
};

export const calculateRowIndexFromTime = (timeStr, interval = 15) => {
	const [hour, minute] = timeStr.split(":").map(Number);
	const totalMinutes = hour * 60 + minute;
	return Math.floor(totalMinutes / interval);
};

export const calculateEndTime = (startTime, duration) => {
	const [hours, minutes] = startTime.split(":").map(Number);
	const totalMinutes = hours * 60 + minutes + Number(duration);
	const newHours = Math.floor(totalMinutes / 60) % 24;
	const newMinutes = totalMinutes % 60;
	return `${String(newHours).padStart(2, "0")}:${String(newMinutes).padStart(
		2,
		"0"
	)}`;
};

export const extractHHMM = (timeString) => {
	if (!timeString) return "";
	const [hours, minutes] = timeString.split(":");
	return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
};



// Helper to generate a random pastel color
export const getRandomColor = () => {
    const pastel = Math.floor(Math.random() * 127) + 127;
    const r = Math.floor(Math.random() * pastel);
    const g = Math.floor(Math.random() * pastel);
    const b = Math.floor(Math.random() * pastel);
    return `rgb(${r},${g},${b})`;
};






// FORMAT DATE (LIKE 12-31-2025) START 
export const formatDate = (dateInput) => {
	if (!dateInput) return "Invalid Date";
	let date;
	if (typeof dateInput === "string") {
		date = new Date(dateInput);
	} else if (dateInput instanceof Date) {
		date = dateInput;
	} else {
		return "Invalid Date";
	}
	if (isNaN(date.getTime())) return "Invalid Date";
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${month}-${day}-${year}`;
};

export const formatDateForBackend = (dateObj) => {
	const date = ensureDateObject(dateObj);
	const dd = String(date.getDate()).padStart(2, "0");
	const mm = String(date.getMonth() + 1).padStart(2, "0");
	const yyyy = date.getFullYear();
	return `${dd}-${mm}-${yyyy}`;
};
// FORMAT DATE (LIKE 12-31-2025) END

// FORMATE DATE LIKE (July 2025) START	
export const formatMonthYear = (dateString) => {
	if (!dateString) return "N/A";
	const date = new Date(dateString);
	return `${date.toLocaleString("en-US", {
		month: "long",
	})} ${date.getFullYear()}`;
};
// FORMATE DATE LIKE (July 2025) END

// FORMAT DATE LIKE (Thu, Jul 31 | 9:37 am) START 
export const formatBookingDateTime = (dateString) => {
	const date = new Date(dateString);
	const options = { weekday: "short", month: "short", day: "numeric" };
	const timeOptions = { hour: "numeric", minute: "2-digit" };
	const formattedDate = date.toLocaleDateString("en-US", options);
	const formattedTime = date
		.toLocaleTimeString("en-US", timeOptions)
		.replace("AM", "am")
		.replace("PM", "pm");
	return `${formattedDate} | ${formattedTime}`;
};
// FORMAT DATE LIKE (Thu, Jul 31 | 9:37 am) END 

// FORMAT DATE LIKE (Thu, Jul 31 at 9:37 AM) START 
export const formatDateTime = (isoDateString) => {
	const date = new Date(isoDateString);
	if (isNaN(date)) return "-";

	const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
	const months = [
		"Jan",
		"Feb",
		"Mar",
		"Apr",
		"May",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Oct",
		"Nov",
		"Dec",
	];
	const dayName = days[date.getDay()];
	const month = months[date.getMonth()];
	const day = date.getDate();
	const hours = date.getHours();
	const minutes = date.getMinutes();
	const ampm = hours >= 12 ? "PM" : "AM";
	const formattedHour = hours % 12 === 0 ? 12 : hours % 12;
	const formattedMin = minutes.toString().padStart(2, "0");

	return `${dayName}, ${month} ${day} at ${formattedHour}:${formattedMin} ${ampm}`;
};
// FORMAT DATE LIKE (Thu, Jul 31 at 9:37 AM) END

// FORMAT DATE INTO STRING LIKE "July 2" START
export const formatDateIntoString = (datetime) => {
  const date = new Date(datetime);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  });
};
// FORMAT DATE INTO STRING LIKE "July 2" END

// FORMAT DATE LIKE (June 6, 2025) START 
export const formatDateLong = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date)) return '';
  
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};
// FORMAT DATE LIKE (June 6, 2025) END 

// FORMAT DATE LIKE [DD-MM-YYYY] START
export const formatDateToDDMMYYYY = (date) => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const year = d.getFullYear();

  return `${day}-${month}-${year}`;
};
// FORMAT DATE LIKE [DD-MM-YYYY] END



// Map backend sale/booking status to UI label and color
// Case-insensitive; defaults to Pending when unknown
export function mapSaleStatus(rawStatus) {
  const input = (rawStatus || '').toString().trim().toLowerCase();

  // Normalize common variants
  const normalized = input
    .replace(/\s+/g, ' ') // collapse spaces
    .replace(/-/g, '-') // keep dashes
    .trim();

  let label = 'Pending';

  // Special mappings - only these three get renamed
  if (normalized === 'completed' || normalized === 'paid') {
    label = 'Closed';
  } else if (normalized === 'partial') {
    label = 'Payment In Progress';
  } else if (
    normalized === 'pending' ||
    normalized === 'check-in (in progress)' ||
    normalized === 'checked-in' ||
    normalized === 'checkin (in progress)'
  ) {
    label = 'Open';
  } 
  // All other statuses show as-is (properly capitalized)
  else if (normalized === 'voided') {
    label = 'Voided';
  } else if (normalized === 'unconfirmed') {
    label = 'Unconfirmed';
  } else if (normalized === 'confirmed') {
    label = 'Confirmed';
  } else if (normalized === 'canceled' || normalized === 'cancelled' || normalized === 'cancelled (refunded)') {
    label = 'Cancelled';
  } else if (normalized === 'holding') {
    label = 'Holding';
  } else {
    // For any unknown status, capitalize first letter
    label = input.charAt(0).toUpperCase() + input.slice(1);
  }

  // Colors spec
  let color = '#CED4D8'; // default grey
  if (label === 'Open') color = '#B0D9E9';
  if (label === 'Payment In Progress') color = '#FFE9A0'; // Yellow/orange for partial payment
  // Voided and Pending remain #CED4D8
  // Closed not specified in spec; keep default grey unless defined later

  return { label, color };
}



// FORMAT DATE [2025-08-07 17:30:PM] INTO [Thu, Aug 7 at 5:30 PM] START 
export function formatReadableDateTime(inputDateTime) {
  if (!inputDateTime) return "";

  const raw = String(inputDateTime).trim();

  // Helper to safely build a local Date
  const buildLocalDate = (yyyy, mm, dd, HH, MM, SS = "00") => {
    // Ensure two-digits
    const m = String(mm).padStart(2, "0");
    const d = String(dd).padStart(2, "0");
    const h = String(HH).padStart(2, "0");
    const mi = String(MM).padStart(2, "0");
    const s = String(SS).padStart(2, "0");
    // Use T to avoid timezone coercion; no Z means local time
    return new Date(`${yyyy}-${m}-${d}T${h}:${mi}:${s}`);
  };

  let date;

  // Case 1: DD-MM-YYYY HH:mm(:ss)? [AM|PM]
  let m = raw.match(/^([0-9]{2})-([0-9]{2})-([0-9]{4})\s+([0-9]{1,2}):([0-9]{2})(?::([0-9]{2}))?\s*(AM|PM)?$/i);
  if (m) {
    const [, dd, mm, yyyy, hhStr, minStr, secStr = "00", ampm] = m;
    let hh = parseInt(hhStr, 10);
    if (ampm) {
      const up = ampm.toUpperCase();
      if (up === "PM" && hh !== 12) hh += 12;
      if (up === "AM" && hh === 12) hh = 0;
    }
    date = buildLocalDate(yyyy, mm, dd, hh, parseInt(minStr, 10), secStr);
  }

  // Case 2: YYYY-MM-DD HH:mm(:ss)? [AM|PM] (sometimes malformed like 17:30:PM)
  if (!date) {
    // Normalize the odd "HH:mm:AM" → "HH:mm AM"
    const normalized = raw.replace(/(\d{1,2}:\d{2}):\s*(AM|PM)$/i, "$1 $2");
    m = normalized.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})\s+([0-9]{1,2}):([0-9]{2})(?::([0-9]{2}))?\s*(AM|PM)?$/i);
    if (m) {
      const [, yyyy, mm, dd, hhStr, minStr, secStr = "00", ampm] = m;
      let hh = parseInt(hhStr, 10);
      if (ampm && hh <= 12) { // only convert when in 12h range
        const up = ampm.toUpperCase();
        if (up === "PM" && hh !== 12) hh += 12;
        if (up === "AM" && hh === 12) hh = 0;
      }
      date = buildLocalDate(yyyy, mm, dd, hh, parseInt(minStr, 10), secStr);
    }
  }

  // Fallback: try native Date directly
  if (!date) {
    date = new Date(raw);
  }

  if (isNaN(date)) return "";

  const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${weekday}, ${month} ${day} at ${time}`;
}
// FORMAT DATE [2025-08-07 17:30:PM] INTO [Thu, Aug 7 at 5:30 PM] END

// FORMAT DATE [16-08-2025] INTO [Tue, Aug 8] START 
export function formatDateDayMonth(dateString) {
  if (!dateString) return "";

  const [day, month, year] = dateString.split("-").map(Number);
  if (!day || !month || !year) return "";

  const date = new Date(year, month - 1, day);

  // Format: "Sat, Aug 16"
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}
// FORMAT DATE [16-08-2025] INTO [Tue, Aug 8] END

// REMOVE SECONDS FROM TIME START 
export function removedSeconds(timeString) {
  if (!timeString) return "";

  // Split by ":" → ["11", "15", "00 AM"]
  const [hours, minutes, rest] = timeString.split(":");

  // Keep only hours and minutes + AM/PM part
  return `${hours}:${minutes} ${rest.split(" ")[1]}`;
}
// REMOVE SECONDS FROM TIME END


// FORMAT DATE [2025-09-25T07:08:50.000000Z] or [2026-03-10 18:05:47] INTO [Tue, Mar 10 at 6:05 PM] START
export function formatDateWithTime(dateString) {
  if (!dateString) return "";

  // Normalize "YYYY-MM-DD HH:mm:ss" to "YYYY-MM-DDTHH:mm:ss" for reliable parsing
  const normalized = String(dateString).replace(" ", "T");
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return String(dateString);

  const weekday = date.toLocaleString("en-US", { weekday: "short" });
  const month = date.toLocaleString("en-US", { month: "short" });
  const day = date.getDate();
  const hour = date.getHours();
  const minute = date.getMinutes();

  // Convert to 12-hour format with AM/PM
  const period = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
  const formattedMinute = String(minute).padStart(2, "0");

  return `${weekday}, ${month} ${day} at ${formattedHour}:${formattedMinute} ${period}`;
}
// FORMAT DATE [2025-09-25T07:08:50.000000Z] INTO LIKE [ Thu, Sep 25 at 2:15 PM] END


// FORMAT DATE [Tue Dec 02 2025 00:00:00 GMT+0530 (India Standard Time)] INTO [2025-12-02] START
export const formatDateToYMD = (date) => {
	if (!date) return "";
	const d = new Date(date);
	if (isNaN(d)) return "";
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	
	return `${year}-${month}-${day}`;
};
// FORMAT DATE [Tue Dec 02 2025 00:00:00 GMT+0530 (India Standard Time)] INTO [2025-12-02] END
  

export const generateFullDaySlots12Hour = (interval) => {
	const slots = [];
	let currentTime = new Date("1970-01-01T00:00:00");

	// We stop before reaching the next day
	const endTime = new Date("1970-01-01T24:00:00");

	while (currentTime < endTime) {
	  const timeString = currentTime.toTimeString().slice(0, 5); // "HH:mm"
	  const formattedTime = convertTo12Hour(timeString);
	  const isBold = currentTime.getMinutes() === 0;

	  slots.push({ time: formattedTime, time24: timeString, isBold });

	  currentTime.setMinutes(currentTime.getMinutes() + interval);

	  // ❗ Prevent infinite loop
	  if (slots.length > 200) {
		// console.warn("Too many slots generated. Stopping loop.");
		break;
	  }
	}

	return slots;
  };


//   GET INTIALS OF NAME START 
export const getInitials = (name) => {
	if (!name) return "N/A";
	return name
	.split(" ")
	.map((n) => (n ? n[0].toUpperCase() : ""))
	.join("");
};
//   GET INTIALS OF NAME END

// Generate consistent color based on user ID or name
export const getConsistentColor = (userId, name = '') => {
    // Use user ID if available, otherwise use name
    const seed = userId ? String(userId) : (name || 'default');
    
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        const char = seed.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    
    const r = Math.abs(hash % 120) + 80;
    const g = Math.abs((hash * 7) % 120) + 80;
    const b = Math.abs((hash * 13) % 120) + 80;
    
    return `rgb(${r},${g},${b})`;
}; 


export const timeToMinutes = (timeStr = "") => {
	if (typeof timeStr !== "string" || !timeStr.includes(":")) {
		return NaN;
	}
	const [h, m] = timeStr.split(":");
	const hours = parseInt(h, 10);
	const mins = parseInt(m, 10);
	return isNaN(hours) || isNaN(mins) ? NaN : hours * 60 + mins;
};


/**
 * Normalize a time string (12h "hh:mm AM/PM" ya 24h "HH:mm") into minutes since midnight
 * @param {string} time - e.g. "10:15 AM" or "22:30"
 * @returns {number} minutes since midnight (0–1439)
 */
export const normalizeTime = (time) => {
  if (!time || typeof time !== "string") return 0;

  // Match "hh:mm" with optional AM/PM
  const match = time.match(/(\d+):(\d+)\s*(AM|PM)?/i);
  if (!match) {
    // Fallback for plain "HH:mm"
    const [h, m] = time.split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
  }

  let [, hourStr, minuteStr, meridian] = match;
  let hour = parseInt(hourStr, 10);
  let minute = parseInt(minuteStr, 10);

  if (meridian) {
    if (meridian.toUpperCase() === "PM" && hour !== 12) hour += 12;
    if (meridian.toUpperCase() === "AM" && hour === 12) hour = 0;
  }

  return hour * 60 + minute;
};

/**
 * Cash rounding function following Singapore rounding rules:
 * - Amounts ending in 1 or 2 cents → round down to nearest 10 cents
 * - Amounts ending in 3 or 4 cents → round up to nearest 5 cents
 * - Amounts ending in 6 or 7 cents → round down to nearest 5 cents
 * - Amounts ending in 8 or 9 cents → round up to nearest 10 cents
 * @param {number|string} amount - The amount to round
 * @returns {number} The rounded amount
 */
export const roundCashAmount = (amount) => {
	const num = parseFloat(amount);
	if (isNaN(num)) return amount;
	
	const cents = Math.round((num * 100) % 100);
	const dollars = Math.floor(num);
	const lastDigit = cents % 10; // Get the last digit (1-9)
	
	let roundedCents;
	if (lastDigit === 1 || lastDigit === 2) {
		// Round down to nearest 10 cents
		roundedCents = Math.floor(cents / 10) * 10;
	} else if (lastDigit === 3 || lastDigit === 4) {
		// Round up to nearest 5 cents
		roundedCents = Math.floor(cents / 10) * 10 + 5;
	} else if (lastDigit === 6 || lastDigit === 7) {
		// Round down to nearest 5 cents
		roundedCents = Math.floor(cents / 10) * 10 + 5;
	} else if (lastDigit === 8 || lastDigit === 9) {
		// Round up to nearest 10 cents
		roundedCents = Math.ceil(cents / 10) * 10;
		// If roundedCents is 100, it means we've gone to the next dollar
		if (roundedCents >= 100) {
			return dollars + 1 + 0.0; // Next dollar
		}
	} else {
		// 0 or 5 - no change needed
		roundedCents = cents;
	}
	
	return dollars + (roundedCents / 100);
};



// Helper function to check if service time is before 6PM (18:00)
export const isTimeBefore6PM = (startTime) => {
	if (!startTime) return false;
	const startMinutes = convertToMinutes(startTime);
	const sixPM = 18 * 60 + 1; // 18:1 in minutes
	return startMinutes < sixPM;
};

/** True when start is 6:00 PM (18:00) or later. */
export const isTimeAtOrAfter6PM = (startTime) => {
	if (!startTime) return false;
	const startMinutes = convertToMinutes(extractHHMM(startTime) || startTime);
	if (Number.isNaN(startMinutes)) return false;
	return startMinutes >= 18 * 60;
};

/** Early-morning clocks that may belong to the next calendar day (before 6:00 AM). */
export const EARLY_MORNING_DAY_CUTOFF_MINUTES = 6 * 60;

/**
 * True when a service STARTS on another day vs selected date, is a real overnight
 * span (ends after midnight on the next calendar day), or follows a parent that
 * already starts next-day. Ending exactly at 00:00 alone does NOT count — that is
 * still a same-day evening booking (e.g. 11 PM–12 AM).
 */
export const isServiceEligibleForCrossDayTimeSelection = (service,selectedDateStr = null, previousService = null) => {
	if (!service) return false;

	const selectedNormalized = selectedDateStr ? String(selectedDateStr) : null;
	const startDate = service?.start_date ? String(service.start_date) : null;
	const endDate = service?.end_date ? String(service.end_date) : null;
	const endMinutes = convertToMinutes(extractHHMM(service?.end_time || ""));

	// Starts on a different calendar day than the date being edited/created.
	if (selectedNormalized && startDate) {
		const offset = getSignedDayOffsetFromDateStrings(startDate, selectedNormalized);
		if (offset !== 0) return true;
	}

	if (typeof service?.dayOffset === "number" && !Number.isNaN(service.dayOffset) && service.dayOffset !== 0) {
		return true;
	}
	if (
		typeof service?.absoluteMinutes === "number" && !Number.isNaN(service.absoluteMinutes) &&
		(service.absoluteMinutes >= 1440 || service.absoluteMinutes < 0)
	) {
		return true;
	}

	// Real overnight: end_date after start_date AND some duration after midnight (not exactly 00:00).
	if (startDate && endDate && startDate !== endDate && !Number.isNaN(endMinutes) && endMinutes > 0) {
		return true;
	}

	const segments = Array.isArray(service?.cross_day_booking_item) ? service.cross_day_booking_item : [];
	if (segments.length >= 2) {
		const uniqueDates = new Set(
			segments.map((segment) => segment?.start_date).filter(Boolean).map(String)
		);
		if (uniqueDates.size >= 2) {
			const firstStart = extractHHMM(segments[0]?.start_time || "");
			const firstEnd = extractHHMM(segments[0]?.end_time || "");
			const hasDistinctTimes = segments.some((segment) => (
				extractHHMM(segment?.start_time || "") !== firstStart ||
				extractHHMM(segment?.end_time || "") !== firstEnd
			));
			if (hasDistinctTimes) return true;
		}
	}

	if (previousService && service?.linkedToPrevious !== false) {
		return isServiceEligibleForCrossDayTimeSelection(previousService, selectedDateStr, null);
	}

	return false;
};

/**
 * Whether later services may show / use next-day early-morning (+1) options.
 * Only when the chain is already cross-day / split — not merely because first is ≥ 6 PM.
 */
export const canLaterServiceUseNextDayEarlyMorning = (firstService, selectedDateStr = null, previousService = null) => (
	isServiceEligibleForCrossDayTimeSelection(firstService, selectedDateStr, previousService)
);

/**
 * Day offset for create/edit service time dropdowns.
 * - Explicit +1 option always wins.
 * - Same-day non-split bookings: early morning stays on selected day (no silent +1).
 * - Cross-day / split / prev-next-date services: early morning → +1 (caller may refine with nearest).
 */
export const resolveMultiServiceTimeDayOffset = ({
	clockTime24,
	explicitDayOffset = 0,
	scheduleIndex = 0,
	firstService = null,
	currentService = null,
	previousService = null,
	selectedDateStr = null,
} = {}) => {
	if (Number(explicitDayOffset) > 0) return 1;

	const clock = convertToMinutes(extractHHMM(clockTime24) || clockTime24);
	if (Number.isNaN(clock)) return 0;

	// First service: stay on selected day unless user picked +1.
	if (!Number.isFinite(scheduleIndex) || scheduleIndex <= 0) return 0;

	const isEarlyMorning = clock < EARLY_MORNING_DAY_CUTOFF_MINUTES;
	if (!isEarlyMorning) return 0;

	const eligible =
		isServiceEligibleForCrossDayTimeSelection(currentService, selectedDateStr, previousService) ||
		isServiceEligibleForCrossDayTimeSelection(firstService, selectedDateStr, null);

	return eligible ? 1 : 0;
};

export const normalizeDateInput = (input) => {
	if (!input) return null;
	if (input instanceof Date) return input;
	if (typeof input === "string") {
		if (/^\d{2}-\d{2}-\d{4}$/.test(input)) {
			const [dd, mm, yyyy] = input.split("-");
			return new Date(`${yyyy}-${mm}-${dd}`);
		}
		if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
			return new Date(input);
		}
	}
	const parsed = new Date(input);
	return isNaN(parsed) ? null : parsed;
};
// Helper function to check if a date is a weekday (Monday-Friday)
export const isWeekday = (dateInput) => {
	if (!dateInput) return false;
	const normalizedDate = normalizeDateInput(dateInput);
	if (!normalizedDate) return false;
	const dayOfWeek = normalizedDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
	return dayOfWeek >= 1 && dayOfWeek <= 5; // Monday (1) through Friday (5)
};

/**
 * Keeps `isMemberRateApplied` identical on every service in an ordered list, matching the first
 * service (index 0). Use for single-booking `selectedServices` or one pax lane's `selectedServices`.
 *
 * The anchor is normally taken from `services[0].isMemberRateApplied`. Pass `options.anchor` when
 * you have just recomputed the flag for the first service only (e.g. membership / holiday / time rules).
 *
 * @param {Array<Object>} services
 * @param {Object} [options]
 * @param {boolean} [options.anchor] - Override anchor; when omitted, uses `services[0].isMemberRateApplied`
 * @param {(service: Object, isMemberRateApplied: boolean) => number} [options.getPrice] - If set, assigns `price` from this
 * @returns {Array<Object>} New array (does not mutate `services`)
 */
/** Outlet / attendance business timezone (Singapore). */
export const APP_TIMEZONE = "Asia/Singapore";
/** YYYY-MM-DD calendar key for a date in the given timezone. */
export const getDateKeyInTimezone = (dateInput, timeZone = APP_TIMEZONE) => {
	const date = ensureDateObject(dateInput);
	if (!date || isNaN(date.getTime())) return "";
	return new Intl.DateTimeFormat("en-CA", {timeZone, year: "numeric", month: "2-digit", day: "2-digit", }).format(date);
};
/** Current HH:mm in the given timezone (matches outlet / API attendance times). */
export const getCurrentTimeInTimezone = (timeZone = APP_TIMEZONE) => {
	const parts = new Intl.DateTimeFormat("en-GB", {timeZone, hour: "2-digit", minute: "2-digit", hour12: false,}).formatToParts(new Date());
	let hour = parts.find((part) => part.type === "hour")?.value ?? "00";
	const minute = parts.find((part) => part.type === "minute")?.value ?? "00";
	if (hour === "24") hour = "00";
	return `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
};

export const isPastDateInTimezone = (dateInput, timeZone = APP_TIMEZONE) => {
	const todayKey = getDateKeyInTimezone(new Date(), timeZone);
	const checkKey = getDateKeyInTimezone(dateInput, timeZone);
	return checkKey < todayKey;
};

export const isFutureDateInTimezone = (dateInput, timeZone = APP_TIMEZONE) => {
	const todayKey = getDateKeyInTimezone(new Date(), timeZone);
	const checkKey = getDateKeyInTimezone(dateInput, timeZone);
	return checkKey > todayKey;
};

export const isTodayOrPastInTimezone = (dateInput, timeZone = APP_TIMEZONE) => {
	const todayKey = getDateKeyInTimezone(new Date(), timeZone);
	const checkKey = getDateKeyInTimezone(dateInput, timeZone);
	return checkKey <= todayKey;
};

export function syncMemberRateAppliedFromFirstServices(services, options = {}) {
	if (!Array.isArray(services) || services.length === 0) {
		return services;
	}
	const { getPrice } = options;
	const anchor =
		typeof options.anchor === "boolean"
			? options.anchor
			: Boolean(services[0]?.isMemberRateApplied);
	return services.map((service) => {
		const next = { ...service, isMemberRateApplied: anchor };
		return getPrice ? { ...next, price: getPrice(service, anchor) } : next;
	});
}

/** Hosts where checkout voucher "add" UI is visible (local + QA/dev). Hidden on all other hosts. */
export const CHECKOUT_VOUCHER_ADD_VISIBLE_HOSTS = [
	"localhost:3000",
	"dev.outlet.natureland.hipster-virtual.com",
];

/**
 * True when checkout voucher add UI should be shown (allowlisted hosts only).
 * Uses `window.location.host` (hostname + port) so `localhost:3000` matches.
 * @param {string} [host] - Override; defaults to window.location.host in the browser.
 */
export const isCheckoutVoucherAddVisibleHost = (host) => {
	const allowedHosts = CHECKOUT_VOUCHER_ADD_VISIBLE_HOSTS
		.map((entry) => String(entry).trim().toLowerCase())
		.filter(Boolean);

	if (allowedHosts.length === 0) return false;

	const isAllowed = (value) => {
		const normalized = String(value || "").trim().toLowerCase();
		if (!normalized) return false;
		return allowedHosts.some(
			(target) => normalized === target || normalized.split(":")[0] === target
		);
	};

	if (host != null && String(host).trim() !== "") {
		return isAllowed(host);
	}

	if (typeof window === "undefined") return false;

	const currentHost = String(window.location.host || "").trim().toLowerCase();
	const currentHostname = String(window.location.hostname || "").trim().toLowerCase();

	return isAllowed(currentHost) || isAllowed(currentHostname);
};

export const isBookingAlreadyCompletedResponse = (response) => {
	const status =response?.data?.status ??response?.data?.data?.status ??response?.response?.data?.data?.status ??response?.status;
	return String(status ?? "").trim().toLowerCase() === "completed";
};
